"use client";

import { useCallback, useEffect, useState } from "react";

import { completeWebPushInstallIntent } from "@/lib/completeWebPushInstallIntent";
import { readWebPushInstallIntent } from "@/lib/readWebPushInstallIntent";
import { detectCurrentWebPushCapability } from "@/lib/web-push-browser";
import {
  WEB_PUSH_INSTALL_INTENT_EVENT,
  type WebPushInstallIntent,
} from "@/lib/webPushInstallIntent";

interface StandaloneNavigator extends Navigator {
  standalone?: boolean;
}

function isSupportedStandaloneWebPush() {
  const standaloneDisplay = window.matchMedia("(display-mode: standalone)").matches;
  const standaloneNavigator = navigator as StandaloneNavigator;
  const standalone = standaloneDisplay || standaloneNavigator.standalone === true;
  if (!standalone) return false;

  return detectCurrentWebPushCapability()?.kind === "supported";
}

export function useWebPushInstallIntent() {
  const [state, setState] = useState<{ intent: WebPushInstallIntent | null; resolved: boolean }>({
    intent: null,
    resolved: false,
  });

  useEffect(() => {
    const refresh = () => {
      const intent = readWebPushInstallIntent();
      setState({
        intent: intent && isSupportedStandaloneWebPush() ? intent : null,
        resolved: true,
      });
    };
    refresh();
    window.addEventListener(WEB_PUSH_INSTALL_INTENT_EVENT, refresh);
    window.addEventListener("pageshow", refresh);
    return () => {
      window.removeEventListener(WEB_PUSH_INSTALL_INTENT_EVENT, refresh);
      window.removeEventListener("pageshow", refresh);
    };
  }, []);

  const complete = useCallback(() => {
    completeWebPushInstallIntent();
    setState({ intent: null, resolved: true });
  }, []);

  return { complete, intent: state.intent, resolved: state.resolved };
}
